<template>
  <banner-header-list>
    <template #create>
      <div @click="openItemModal(null)" class="btn btn-sm btn-success ms-auto">Создать компанию</div>
    </template>
  </banner-header-list>
  <div v-if="!loaded" class="card card-body p-0 border-top border-top-1 border-gray-200">
    <div class="table-responsive custom-table">
      <table class="table table-hover table-rounded table-striped table-row-dashed fs-7 table-row-gray-300 gy-5 custom-table align-middle mb-0">
        <thead>
        <tr class="fw-bold fs-8 text-gray-400">
          <th>ID</th>
          <th>Name</th>
          <th>Статус</th>
          <th>Приоритет</th>
          <th>Тип правила</th>
          <th>Начало</th>
          <th>Окончание</th>
          <th class="text-end">Действие</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, key) in campaigns" :key="key">
          <td>{{ item.id }}</td>
          <td>{{ item.name }}</td>
          <td>{{ dictLabel(dictStatuses, item.status) }}</td>
          <td>{{ item.priority }}</td>
          <td>{{ dictLabel(dictRuleTypes, item.ruleType) }}</td>
          <td>{{ formatDate(item.startAt) }}</td>
          <td>{{ formatDate(item.endAt) }}</td>
          <td class="text-end">
            <div class="d-flex justify-content-end">
              <span @click="openItemModal(item.id)" class="ps-2 ms-1 py-1 cursor-pointer text-hover-primary" title="Редактировать">
                <i class="ki-outline ki-pencil fs-5"></i>
              </span>
            </div>
          </td>
        </tr>
        <tr v-if="!campaigns || campaigns.length === 0">
          <td colspan="8" class="text-center py-8 text-gray-500">Список пуст</td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="text-center border-top py-3">
      <div class="btn-group">
        <button class="btn btn-sm btn-light" :disabled="page === 0" @click="goToPage(page - 1)">
          <i class="ki-outline ki-arrow-left"></i>
        </button>
        <button
          v-for="(p, i) in visiblePages"
          :key="i"
          class="btn btn-sm btn-light"
          :class="{
            'text-gray-600': p !== '...' && page !== p,
            'text-success fw-bold': p !== '...' && page === p,
            'btn-disabled text-muted': p === '...'
          }"
          :disabled="p === '...'"
          @click="p !== '...' && goToPage(p)"
        >
          {{ p === '...' ? '...' : p + 1 }}
        </button>
        <button class="btn btn-sm btn-light" :disabled="page === totalPages - 1 || totalPages === 0" @click="goToPage(page + 1)">
          <i class="ki-outline ki-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
  <div v-else class="card rounded">
    <loader />
  </div>
  <company-modal
    v-if="showModal"
    :item-data="selectedItem"
    :dict-statuses="dictStatuses"
    :dict-rule-types="dictRuleTypes"
    @closeModal="closeModal"
  />
</template>

<script>
import BannerHeaderList from '@/views/EnergoGoClub/Banner/BannerHeaderList'
import CompanyModal from '@/views/EnergoGoClub/Banner/Company/CompanyModal'
import Loader from '@/components/Loader.vue'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'

const API_BASE = '/energo-inapp-messaging/api/admin/v1'

function normalizeDictList(data) {
  const raw = Array.isArray(data) ? data : (data?.content ?? data?.items ?? []);
  return raw
    .map((x) => ({
      code: x?.code ?? x?.value ?? x?.id,
      label: x?.label ?? x?.name ?? x?.title ?? (x?.code != null ? String(x.code) : ''),
    }))
    .filter((x) => x.code != null && x.code !== '');
}

export default {
  name: 'Company',
  components: { BannerHeaderList, CompanyModal, Loader },
  data() {
    return {
      loaded: false,
      campaigns: [],
      page: 0,
      size: 20,
      totalPages: 1,
      showModal: false,
      selectedItem: null,
      dictStatuses: [],
      dictRuleTypes: [],
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        for (let i = 0; i < total; i += 1) pages.push(i);
      } else {
        pages.push(0);
        if (current > 3) pages.push('...');
        const start = Math.max(1, current - 1);
        const end = Math.min(total - 2, current + 1);
        for (let i = start; i <= end; i += 1) pages.push(i);
        if (current < total - 4) pages.push('...');
        pages.push(total - 1);
      }
      return pages;
    },
  },
  mounted() {
    this.fetchDictionaries();
    this.getCampaigns();
  },
  methods: {
    fetchDictionaries() {
      const urls = [
        `${API_BASE}/dictionaries/statuses`,
        `${API_BASE}/dictionaries/rule-types`,
      ];
      Promise.all(urls.map((url) => axios.get(url)))
        .then(([statusRes, ruleRes]) => {
          this.dictStatuses = normalizeDictList(statusRes?.data);
          this.dictRuleTypes = normalizeDictList(ruleRes?.data);
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: err?.response?.data?.errorDescription ?? err.message,
          });
          this.dictStatuses = [];
          this.dictRuleTypes = [];
        });
    },
    getCampaigns() {
      this.loaded = true;
      axios.get(`${API_BASE}/campaign`, {
        params: {
          page: this.page,
          size: this.size,
        },
      })
        .then((res) => {
          const payload = res?.data;
          const content = Array.isArray(payload?.content) ? payload.content : [];
          this.campaigns = content;
          this.page = payload?.number ?? payload?.pageable?.pageNumber ?? this.page;
          this.totalPages = Math.max(1, Number(payload?.totalPages ?? 1));
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: err?.response?.data?.errorDescription ?? err.message,
          });
          this.campaigns = [];
          this.totalPages = 1;
        })
        .finally(() => {
          this.loaded = false;
        });
    },
    openItemModal(id) {
      if (!id) {
        this.selectedItem = null;
        this.showModalWithBootstrap();
        return;
      }
      axios.get(`${API_BASE}/campaign/${id}`)
        .then((res) => {
          this.selectedItem = res?.data || null;
          this.showModalWithBootstrap();
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: err?.response?.data?.errorDescription ?? err.message,
          });
        })
        .finally(() => {
          this.loaded = false;
        });
    },
    showModalWithBootstrap() {
      this.showModal = true;
      setTimeout(() => {
        const modalEl = document.getElementById('campaignModal');
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }, 10);
    },
    closeModal(isUpdate) {
      this.showModal = false;
      this.selectedItem = null;
      if (isUpdate) this.getCampaigns();
    },
    goToPage(newPage) {
      if (newPage >= 0 && newPage < this.totalPages) {
        this.page = newPage;
        this.getCampaigns();
      }
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    },
    dictLabel(list, code) {
      if (code == null || code === '') return '';
      const found = Array.isArray(list) ? list.find((x) => x.code === code) : null;
      return found?.label ?? String(code);
    },
  },
}
</script>

<style scoped>

</style>
